import { and, desc, eq } from "drizzle-orm";
import { context, propagation, trace } from "@opentelemetry/api";

import type { WorkspaceFeedItemOldestEventsResponse } from "@storyboard/web-client";
import { client } from "@/config";
import { db } from "@/db/db";
import { feed, item } from "@/db/schema";
import { downloadFeedItemContent } from "@/models/actions/initialFeedLoad";
import { bulkUpsertAccountEvent } from "@/models/repositories/AccountEvent";

const EVENTS_PER_PAGE = 10000;

export async function downloadOldestFeedItemEvents(
  itemIds: string[],
  workspaceId: string,
  timestamp?: string,
  deferEmit = false,
  maxPages?: number,
) {
  let page = 0;
  let count = 0;
  const parallelRequests = 1;
  while (true) {
    const promises: Promise<WorkspaceFeedItemOldestEventsResponse>[] = [];

    for (let index = 0; index < parallelRequests; index++) {
      const currentPage = page;
      promises.push(client.getFeedItemOldestEvents(workspaceId, itemIds, currentPage, EVENTS_PER_PAGE, timestamp));
      page += 1;
    }

    const responses = await Promise.all(promises);
    for (const response of responses) {
      if (!response) {
        continue;
      }
      await bulkUpsertAccountEvent(response.events, deferEmit);
    }
    const currentCount = responses.reduce((acc, r) => acc + r?.events.length, 0);
    count += currentCount;
    if (currentCount % EVENTS_PER_PAGE !== 0 || currentCount === 0) {
      return count;
    }

    if (maxPages && page >= maxPages) {
      return count;
    }

    if (page > 1000) {
      throw new Error("Too many pages");
    }
  }
}

const fetchLastTenItems = async (feedId: string): Promise<string[]> => {
  const tracer = trace.getTracer("bootstrap");

  const activeContext = propagation.extract(context.active(), {});
  const span = tracer.startSpan("fetchLastTenItems", {}, activeContext);

  const items = await db.query.item
    .findMany({
      where: and(eq(feed.id, feedId), eq(item.deletedAt, null)),
      orderBy: desc(item.createdAt),
      limit: 10,
    })
    .execute();
  span.end();
  return items?.map((item) => item.id) ?? [];
};

export const fetchFeedItemsAndDownloadEvents = async (feedId: string, workspaceId: string): Promise<number> => {
  const itemsMap = await fetchLastTenItems(feedId);
  if (itemsMap?.length > 0) {
    await downloadFeedItemContent(workspaceId, itemsMap);
  }
  return itemsMap?.length ?? 0;
};
